/*!
 * a11y-patch.js — runtime accessibility fixes
 * No dependencies. Uses jQuery only if the theme already loaded it.
 *
 * Fixes: missing form labels, duplicate IDs, aria-hidden labels,
 *        phantom tab stops, empty links/buttons, missing alt,
 *        broken aria-controls / aria-labelledby / aria-describedby.
 *
 * Re-runs after facet AJAX, section reloads, and any DOM mutation.
 */
(function () {
  'use strict';

  var running = false;
  var COMPONENT = 'details, form, li, .facets__item, .facet-checkbox, .facets__price-row, .js-filter, .card, .card-wrapper';

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function txt(s) {
    return (s == null ? '' : String(s)).replace(/\s+/g, ' ').trim();
  }

  /* Resolve label[for] by comparing attributes rather than building a selector.
     Shopify param names contain dots and colons, which break attribute selectors. */
  function labelsFor(root, id) {
    return all('label', root).filter(function (l) { return l.getAttribute('for') === id; });
  }

  var REFS = ['aria-controls', 'aria-labelledby', 'aria-describedby'];

  function swapToken(el, attr, oldId, newId) {
    var v = el.getAttribute(attr);
    if (!v) return;
    el.setAttribute(attr, v.split(/\s+/).map(function (t) {
      return t === oldId ? newId : t;
    }).join(' '));
  }

  function nearbyText(el) {
    var box = el.closest('.facet-checkbox, .checkbox, .facets__item, li, .facets__price-row, .card, .card-wrapper') || el.parentElement;
    if (!box) return '';
    var clone = box.cloneNode(true);
    all('input, select, textarea, script, style', clone).forEach(function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    return txt(clone.textContent);
  }

  function hasName(el) {
    return !!(txt(el.getAttribute('aria-label')) || txt(el.getAttribute('title')));
  }

  /* ---------- 1. duplicate IDs ---------- */
  /* Themes render the filter set twice (bar + drawer). Identical IDs detach
     the second copy's <label for>, which WAVE counts as a missing label. */
  function fixDuplicateIds() {
    var seen = Object.create(null), n = 0;

    all('[id]').forEach(function (el) {
      var id = el.id;
      if (!id) return;

      if (!seen[id]) { seen[id] = 1; return; }

      seen[id] += 1;
      var newId = id + '--dup' + seen[id];
      var root = el.closest(COMPONENT) || el.parentElement || document;

      labelsFor(root, id).forEach(function (l) { l.setAttribute('for', newId); });

      all('[aria-controls], [aria-labelledby], [aria-describedby]', root).forEach(function (r) {
        REFS.forEach(function (a) { swapToken(r, a, id, newId); });
      });

      el.id = newId;
      n++;
    });

    return n;
  }

  /* ---------- 2. aria-hidden on the only label ---------- */
  function fixHiddenLabels() {
    var n = 0;
    all('label[aria-hidden="true"]').forEach(function (l) {
      l.removeAttribute('aria-hidden');
      l.classList.add('a11y-vh');
      n++;
    });
    return n;
  }

  /* ---------- 3. tab stops ---------- */
  function fixTabindex() {
    var n = 0;

    // tabindex="0" on a plain wrapper adds a silent stop before every control.
    all('div[tabindex="0"], span[tabindex="0"], li[tabindex="0"]').forEach(function (d) {
      if (d.getAttribute('role')) return;
      if (!d.querySelector('input, select, textarea, button, a')) return;
      d.removeAttribute('tabindex');
      n++;
    });

    // tabindex="0" on a link or button is redundant and reorders the tab sequence.
    all('a[href][tabindex="0"], button[tabindex="0"]').forEach(function (el) {
      el.removeAttribute('tabindex');
      n++;
    });

    // tabindex="-1" makes a real control unreachable by keyboard.
    all('a[href][tabindex="-1"], button[tabindex="-1"], input[tabindex="-1"]').forEach(function (el) {
      el.removeAttribute('tabindex');
      n++;
    });

    return n;
  }

  /* ---------- 4. unlabeled form controls ---------- */
  function fixLabels() {
    var n = 0;

    all('input, select, textarea').forEach(function (el) {
      var type = (el.type || '').toLowerCase();
      if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'reset') return;
      if (el.labels && el.labels.length) return;
      if (el.closest('label')) return;
      if (hasName(el)) return;

      var name = nearbyText(el) || txt(el.placeholder) || txt(el.name) || type;
      if (!name) return;

      el.setAttribute('aria-label', name.slice(0, 120));
      n++;
    });

    return n;
  }

  /* ---------- 5. empty links and buttons ---------- */
  function fixEmptyControls() {
    var n = 0;

    all('a[href], button').forEach(function (c) {
      if (txt(c.textContent)) return;
      if (hasName(c)) return;

      var img = c.querySelector('img[alt]');
      if (img && txt(img.getAttribute('alt'))) return;

      var vh = c.querySelector('.visually-hidden, .sr-only, .a11y-vh');
      var label = txt(vh && vh.textContent) || txt(c.getAttribute('href'));
      if (!label) return;

      if (/^https?:|^\//.test(label)) {
        var parts = label.split('?')[0].split('/').filter(Boolean);
        label = txt((parts.pop() || 'link').replace(/[-_]+/g, ' '));
      }

      c.setAttribute('aria-label', label.slice(0, 120));
      n++;
    });

    return n;
  }

  /* ---------- 6. images and svg ---------- */
  function fixImages() {
    var n = 0;

    all('img:not([alt])').forEach(function (i) {
      var link = i.closest('a');
      var card = i.closest('.card, .card-wrapper, .product-card');
      var heading = card && card.querySelector('.card__heading, .card__title, h2, h3');

      var alt = txt(i.getAttribute('title')) ||
                txt(link && link.getAttribute('aria-label')) ||
                txt(heading && heading.textContent);

      i.setAttribute('alt', alt.slice(0, 120));   // empty alt = decorative, still valid
      n++;
    });

    all('svg').forEach(function (s) {
      if (s.getAttribute('role') === 'img') return;
      if (s.querySelector('title')) return;
      if (s.getAttribute('aria-hidden') === 'true') return;
      s.setAttribute('aria-hidden', 'true');
      s.setAttribute('focusable', 'false');
      n++;
    });

    return n;
  }

  /* ---------- 7. broken aria references ---------- */
  function fixBrokenRefs() {
    var n = 0;

    all('[aria-controls], [aria-labelledby], [aria-describedby]').forEach(function (el) {
      REFS.forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (!v) return;

        var ids = v.split(/\s+/).filter(Boolean);
        var live = ids.filter(function (id) { return document.getElementById(id); });
        if (live.length === ids.length) return;

        if (live.length) {
          el.setAttribute(attr, live.join(' '));
        } else {
          el.removeAttribute(attr);   // pointing at nothing is worse than absent
        }
        n++;
      });
    });

    return n;
  }

  /* ---------- css ---------- */
  function injectCss() {
    if (document.getElementById('a11y-patch-css')) return;
    var s = document.createElement('style');
    s.id = 'a11y-patch-css';
    s.textContent =
      '.a11y-vh{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0}';
    document.head.appendChild(s);
  }

  /* ---------- runner ---------- */
  function run() {
    if (running) return null;
    running = true;

    injectCss();

    var report = {
      duplicateIds: fixDuplicateIds(),
      hiddenLabels: fixHiddenLabels(),
      tabindex: fixTabindex(),
      labels: fixLabels(),
      emptyControls: fixEmptyControls(),
      images: fixImages(),
      brokenRefs: fixBrokenRefs()
    };

    report.remainingUnlabeled = all('input, select, textarea').filter(function (el) {
      var t = (el.type || '').toLowerCase();
      if (t === 'hidden' || t === 'submit' || t === 'button' || t === 'reset') return false;
      return !(el.labels && el.labels.length) && !el.closest('label') && !hasName(el);
    }).length;

    if (window.A11Y_DEBUG) console.table(report);

    setTimeout(function () { running = false; }, 60);
    return report;
  }

  window.a11yPatch = run;

  /* ---------- triggers ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  ['shopify:section:load', 'facet:update', 'filter:update'].forEach(function (e) {
    document.addEventListener(e, function () { setTimeout(run, 150); });
  });

  if (window.jQuery) {
    window.jQuery(document).ajaxComplete(function () { setTimeout(run, 150); });
  }

  // Facet filtering swaps the DOM via fetch; re-run once it settles.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function () {
      return nativeFetch.apply(this, arguments).then(function (res) {
        setTimeout(run, 250);
        return res;
      });
    };
  }

  if (window.MutationObserver) {
    var t = null;
    new MutationObserver(function () {
      if (running) return;
      clearTimeout(t);
      t = setTimeout(run, 300);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
 